import {
  any,
  bool,
  func,
  number,
  object,
  string,
  oneOf,
  arrayOf,
  array,
  oneOfType,
} from 'prop-types'
import { Component } from 'react'
import ReactDOM from 'react-dom'
import tippy, { sticky } from 'tippy.js'
import { kuiperClassNames } from '@opallabs/kuiper/src/utils'
import './styles.scss'

function applyIfFunction(fn) {
  return typeof fn === 'function' ? fn() : fn
}

export default class Tooltip extends Component {
  static propTypes = {
    animation: oneOfType([string, bool]),
    appendTo: func,
    arrow: bool,
    children: any,
    className: string,
    content: any,
    delay: array,
    disabled: bool,
    edgeOffset: number,
    flip: bool,
    flipBehavior: arrayOf(string),
    forceClose: bool,
    hideOnClick: oneOf([true, false, 'toggle']),
    inline: bool,
    interactive: bool,
    maxWidth: oneOfType([number, string]),
    onHidden: func,
    onHide: func,
    onMouseEnter: func,
    onShow: func,
    onShown: func,
    open: bool,
    placement: string,
    preventOverflow: bool,
    rawTemplate: any,
    showOnCreate: bool,
    sticky: oneOfType([bool, string]),
    style: object,
    tabIndex: number,
    title: string,
    trigger: string,
    type: oneOf(['dropdown', 'lapidary_dropdown', 'popover', 'tooltip']),
  }

  static defaultProps = {
    disabled: false,
    ignoreAttributes: true,
    hideOnClick: true,
    maxWidth: 'none',
    placement: 'bottom',
    preventOverflow: true,
    title: null,
  }

  constructor(props) {
    super(props)
    this.state = {
      isOpen: false,
    }
  }

  componentDidMount() {
    this._isMounted = true
    this.initTippy()
  }

  componentDidUpdate(prevProps) {
    if (!this.tippy) {
      return
    }

    // enable and disabled
    if (this.props.disabled === false && prevProps.disabled === true) {
      this.tippy.enable()
      return
    }

    if (this.props.disabled === true && prevProps.disabled === false) {
      // Tippy v5+ hides when disable() is called, which some components
      // like Kuiper AnnotationEditor do not expect when using `disabled`
      // So we'll mutate `state.isEnabled` directly to avoid that behavior
      // @see https://github.com/atomiks/tippy.js-react/issues/106
      this.tippy.state.isEnabled = false
      return
    }

    // open
    if (this.props.open === true && prevProps.open === false) {
      this.showTooltip()
    }

    // close
    if (this.props.open === false && prevProps.open === true) {
      this.hideTooltip()
    }

    // force close can only be used to close a popover
    // not to open
    if (this.props.forceClose === true && prevProps.forceClose === false) {
      this.hideTooltip()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    this.destroyTippy()
  }

  toggleTooltip = () => {
    if (this.state.isOpen) {
      this.hideTooltip()
    } else {
      this.showTooltip()
    }
  }

  showTooltip = () => {
    setTimeout(this.tippy.show, 0)
  }

  hideTooltip = () => {
    this.tippy.hide()
  }

  contentRoot = () => {
    if (!this._contentRoot && typeof window === 'object') {
      this._contentRoot = window.document.createElement('div')
    }
    return this._contentRoot
  }

  initTippy = () => {
    const validProps = this.validProps

    tippy(this.tooltipDOM, {
      offset: this.calculateOffset(),
      ...validProps,
      content: this.props.content ? this.contentRoot() : this.props.rawTemplate,
      plugins: [sticky],
      onHidden: () => {
        if (this._isMounted) {
          this.setState({ isOpen: false })
        }
        if (this.props.onHidden) {
          return this.props.onHidden()
        }
      },
      onShow: () => {
        this.setState({ isOpen: true })
        if (this.props.onShow) {
          return this.props.onShow()
        }
      },
      popperOptions: {
        // Ensure interactive popovers are shown when reference element is
        // in an absolutely-positioned container with `overflow: hidden`
        strategy:
          this.props.interactive && !this.props.appendTo ? 'fixed' : 'absolute',
        modifiers: [
          { name: 'computeStyles' },
          {
            name: 'preventOverflow',
            enabled: !!this.props.preventOverflow,
            options: {
              // Keep 5px padding from Popper v1
              padding: 5,
            },
          },
          {
            name: 'flip',
            enabled: !!this.props.flip,
            options: {
              fallbackPlacements: this.props.flipBehavior ?? undefined,
              // Keep 5px padding from Popper v1
              padding: 5,
            },
          },
        ],
      },
    })

    this.tippy = this.tooltipDOM._tippy

    if (this.props.open || this.props.showOnCreate) {
      this.showTooltip()
    }

    if (this.props.disabled) {
      this.tippy.disable()
    }
  }

  destroyTippy = () => {
    this.tippy.destroy()
    this.tippy = null
  }

  calculateOffset = () => {
    const { placement, edgeOffset } = this.props
    if (placement === 'right' || placement === 'left' || isNaN(edgeOffset)) {
      return undefined
    }

    return [0, edgeOffset]
  }

  handleKeyPress = (e) => {
    e.stopPropagation()
    switch (e.which) {
      case 0x0d: // enter
        this.showTooltip()
        break
      case 0x1b: // escape
        this.hideTooltip()
        break
      case 0x20: // space
        // Don't toggle the popover if the user is entering text.
        if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
          this.toggleTooltip()
        }
        break
      default:
        break
    }
  }

  get validProps() {
    // ...rest operator being used here to extract certain props
    /* eslint-disable no-unused-vars */
    const {
      children,
      className,
      disabled,
      edgeOffset,
      flip,
      flipBehavior,
      forceClose,
      inline,
      open,
      preventOverflow,
      style,
      title,
      type,
      onMouseEnter,
      ...validProps
    } = this.props
    /* eslint-enable no-unused-vars */

    return validProps
  }

  render() {
    return (
      <div
        className={this.props.className}
        style={{ position: 'relative', ...this.props.style }}
        tabIndex={this.props.tabIndex}
        title={this.props.title}
      >
        <div
          onMouseEnter={() => {
            this.props.onMouseEnter()
          }}
          onKeyDown={this.handleKeyPress}
          className={kuiperClassNames('popover-tippy-reference')}
          style={{ width: !this.props.inline ? '100%' : 'auto' }}
          ref={(tooltip) => {
            this.tooltipDOM = tooltip
          }}
          role="button"
          tabIndex="0"
        >
          {this.props.children}
          {this.props.content && this.state.isOpen && this.contentRoot()
            ? ReactDOM.createPortal(
                applyIfFunction(this.props.content),
                this.contentRoot()
              )
            : null}
        </div>
      </div>
    )
  }
}
Tooltip.displayName = 'Kuiper: Tooltip ← PopoverBase ← PopoverV2'
